'use client';

import { c as _c } from "react/compiler-runtime";
import { AppearanceSelector, AppearanceSelectorOption } from '@sprd/sprd-component-kit/v2';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { pdpParamsWithSyncAtom } from '@/atoms/pdpParamsAtom';
import { useResolvedAppearances } from '@/components/ProductCard/AppearanceConfigProvider';
import { GetPathNameArgs, getPathname } from '@/i18n/navigation';
import { tracker } from '@/utility/tracking/tracker';
import { ClientSellableAndProductType } from '../utility/pdpGroups';
import { getAvailableSizeIds } from '../utility/trackingData';
type AppearanceSelectionProps = {
  productType: ClientProductType;
  sellable: Sellable;
  sellableGroup?: ClientSellableAndProductType[];
  selectedSizesStockState: ProductType['stockStates'];
  getPathnameArgs: (appearanceId: string, sellableId?: string) => GetPathNameArgs;
};
export const AppearanceSelection = t0 => {
  const $ = _c(30);
  const {
    productType,
    sellable,
    sellableGroup,
    selectedSizesStockState,
    getPathnameArgs
  } = t0;
  const t = useTranslations();
  const router = useRouter();
  const [pdpParams, setPdpParams] = useAtom(pdpParamsWithSyncAtom);
  const selectedAppearanceId = pdpParams?.appearanceId ?? productType.defaultValues.defaultAppearance.id;
  const appearanceFilterList = sellableGroup ? sellableGroup.flatMap(_temp).map(String) : sellable.allowedAppearances.map(String);
  let t1;
  if ($[0] !== productType || $[1] !== sellableGroup) {
    t1 = sellableGroup ? sellableGroup.flatMap(_temp2) : productType.appearanceIds;
    $[0] = productType;
    $[1] = sellableGroup;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const allAppearanceIds = t1;
  let t2;
  if ($[3] !== allAppearanceIds) {
    t2 = new Set(allAppearanceIds);
    $[3] = allAppearanceIds;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = [...t2];
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const uniqueAppearances = useResolvedAppearances(t3);
  let filteredAppearances = uniqueAppearances;
  if (appearanceFilterList?.length) {
    filteredAppearances = uniqueAppearances.filter(appearance => appearanceFilterList.includes(appearance.id));
  }
  let t4;
  if ($[7] !== getPathnameArgs || $[8] !== productType || $[9] !== router || $[10] !== sellable || $[11] !== sellableGroup || $[12] !== setPdpParams) {
    t4 = appearanceId => {
      tracker.trackFeature({
        featureUsed: "choose-color",
        detail: appearanceId
      });
      tracker.trackClientProduct({
        product: {
          appearanceId,
          availableSizes: getAvailableSizeIds(productType, appearanceId)
        }
      });
      const isMatchingSellable = sellable.allowedAppearances.includes(Number(appearanceId));
      if (!sellableGroup || isMatchingSellable) {
        setPdpParams({
          appearanceId
        });
      } else {
        const matchingSellableGroup = sellableGroup.find(sellableAndProductType_1 => sellableAndProductType_1.sellable.allowedAppearances.includes(Number(appearanceId)))?.sellable?.sellableId;
        const pathnameArgs = getPathnameArgs(appearanceId, matchingSellableGroup);
        const pathname = getPathname(pathnameArgs);
        router.replace(pathname);
      }
    };
    $[7] = getPathnameArgs;
    $[8] = productType;
    $[9] = router;
    $[10] = sellable;
    $[11] = sellableGroup;
    $[12] = setPdpParams;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleAppearanceClick = t4;
  let t5;
  if ($[14] !== t) {
    t5 = t("cyoAssortmentDetailPage.selectAppearance");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== filteredAppearances || $[17] !== handleAppearanceClick || $[18] !== selectedAppearanceId || $[19] !== selectedSizesStockState || $[20] !== sellable) {
    let t7;
    if ($[22] !== handleAppearanceClick || $[23] !== selectedAppearanceId || $[24] !== selectedSizesStockState || $[25] !== sellable) {
      t7 = appearance_0 => {
        const appearanceAvailable = selectedSizesStockState.filter(stockState => stockState.appearance.id === appearance_0.id).some(_temp3);
        return <AppearanceSelectorOption key={appearance_0.id} id={appearance_0.id} colors={appearance_0.colors} name={appearance_0.name} size="l" selected={appearance_0.id === selectedAppearanceId} textureImage={appearance_0.textureImage} disabled={!appearanceAvailable} asChild={true} className={clsx(sellable.allowedAppearances.map(String).includes(appearance_0.id) ? "" : "opacity-25")}><button onClick={() => handleAppearanceClick(appearance_0.id)} /></AppearanceSelectorOption>;
      };
      $[22] = handleAppearanceClick;
      $[23] = selectedAppearanceId;
      $[24] = selectedSizesStockState;
      $[25] = sellable;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    t6 = filteredAppearances.map(t7);
    $[16] = filteredAppearances;
    $[17] = handleAppearanceClick;
    $[18] = selectedAppearanceId;
    $[19] = selectedSizesStockState;
    $[20] = sellable;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[27] !== t5 || $[28] !== t6) {
    t7 = <div className="relative -mr-4 -ml-4 overflow-x-auto overflow-y-hidden px-4 no-scrollbar sm:mx-0 sm:overflow-visible sm:px-0"><AppearanceSelector label={t5} className="sm:flex-wrap">{t6}</AppearanceSelector></div>;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
};
function _temp(sellableAndProductType) {
  return sellableAndProductType.sellable.allowedAppearances;
}
function _temp2(sellableAndProductType_0) {
  return sellableAndProductType_0.productType.appearanceIds;
}
function _temp3(stockState_0) {
  return stockState_0.available;
}